import { Component, Inject, Injectable } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { NotificationService as KendoNotificationService } from '@progress/kendo-angular-notification';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    templateUrl: '../modals/confirmation-dialog.html',
    standalone: false
})
export class ConfirmationDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private sanitizer: DomSanitizer
  ) { }

  get safeMessage(): SafeHtml {
    return this.sanitizer.sanitize(1, this.data.message) || '';
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onYesClick(): void {
    this.dialogRef.close(true);
  }
}

@Component({
    templateUrl: '../modals/alert-dialog.html',
    standalone: false
})
export class AlertDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<AlertDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) { }

  onYesClick(): void {
    this.dialogRef.close(true);
  }
}

@Component({
    templateUrl: '../modals/version-modal.html',
    standalone: false
})
export class VersionDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    private dialogRef: MatDialogRef<any>
  ) {
  }

  refresh() {
    this.dialogRef.close();
  }
}

// see https://stackblitz.com/edit/angular-material-notification-service?file=src%2Fapp%2Fnotification.service.ts
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    private readonly snackBar: MatSnackBar,
    private kendoNotificationService: KendoNotificationService,
    public dialog: MatDialog
  ) {}

  /**
   * Presents a toast displaying the message with a green background
   * @param message Message to display
   * @example
   * this.notificationService.success("confirm oked");
   */
  success(message: string) {
    this.openSnackBar(message, '', 'success-snackbar');
    //this.openKendoSnackBar(message, "success");
  }

  /**
   * Presents a toast displaying the message with a red background
   * @param message Message to display
   * @param duration how long the message should display
   * @example
   * this.notificationService.error("confirm canceled");
   */
  error(message: string, duration = 6000) {
    this.openSnackBar(message, '', 'error-snackbar', duration);
    //this.openKendoSnackBar(message, "error");
  }

  /**
   * Shows a confirmation modal, presenting the user with
   * an OK and Cancel button.
   * @param message Body of the modal
   * @param okCallback Optional function to call when the user clicks Ok
   * @param cancelCallback Option function to call when the user clicks Cancel
   * @param title Optional modal title
   * @example
   * //displays a success or error message depending on what button is clicked.
   * this.notificationService.confirmation(
   * 'it will be gone forever', //message body
   * () => { //okCallback
   *   this.notificationService.success("confirm oked");
   * },
   * () => { //cancelCallback
   *   this.notificationService.error("confirm canceled");
   * });
   */
  confirmation(
    message: string,
    okCallback: () => void,
    cancelCallback: () => any = () => {},
    title = 'Are you sure?',
  ) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: { message, title, okButtonLabel: 'Ok', cancelButtonLabel: 'Cancel' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && okCallback) {
        okCallback();
      }
      if (!result && cancelCallback) {
        cancelCallback();
      }
    });
  }

  /**
   * Shows a confirmation modal, presenting the user with
   * an OK and Cancel button.
   * @param message Body of the modal
   * @param title Optional modal title
   * @param okButtonLabel Optional label for the ok button
   * @param cancelButtonLabel Optional label for the cancel button
   * @returns A Promise<boolean> with user's selection
   * @example
   * this.notificationService.confirmationPromise(
   * 'it will be gone forever', //message body
   * ).then(response => { });
   * - OR -
   * let response = await this.notificationService.confirmationPromise(
   * 'it will be gone forever', //message body
   * );
   */
  confirmationPromise(
    message: string,
    title = 'Are you sure?',
    okButtonLabel: string = 'Yes',
    cancelButtonLabel: string = 'No',
  ): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: { message, title, okButtonLabel, cancelButtonLabel },
    });
    return dialogRef.afterClosed().toPromise();
  }

  /**
   * Shows a modal, presenting the user with an OK button.
   * @param message Body of the modal
   * @param okCallback Optional function to call when the user clicks Ok
   * @param title Optional modal title
   * @example
   * //displays a success when the Ok button is clicked.
   *  this.notificationService.alert("an alert", "notice", () => {
   *    this.notificationService.success("alert oked");
   *  });
   */
  alert(message: string, title = 'Notice', okCallback: () => void = () => {}) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '600px',
      data: { message, title },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && okCallback) {
        okCallback();
      }
    });
  }

  /**
   * Displays a toast with provided message
   * @param message Message to display
   * @param action Action text, e.g. Close, Done, etc
   * @param className Optional extra css class to apply
   * @param duration Optional number of SECONDS to display the notification for
   */
  openSnackBar(
    message: string,
    action: string,
    className = null,
    duration = 6000
  ) {
    let config: MatSnackBarConfig = {
      duration,
      panelClass: [className],
      politeness: 'polite',
    };
    this.snackBar.openFromComponent(CustomSnackBarComponent, {
      data: message,
      ...config
    });
  }

  /**
   * Displays a kendo toast with provided message
   * @param message Message to display
   * @param type Message type to show. Must be one of "none" | "success" | "warning" | "error" | "info"
   */
  openKendoSnackBar(
    message: string,
    type: "none" | "success" | "warning" | "error" | "info",
  ) {
    this.kendoNotificationService.show({
      content: message,
      cssClass: "button-notification",
      animation: { type: "slide", duration: 400 },
      position: { horizontal: "center", vertical: "bottom" },
      type: { style: type, icon: true },
      closable: true,
    });
  }
  
  showVersionDialog() {
    const dialogRef = this.dialog.open(VersionDialogComponent, {
      width: '600px',
      disableClose: true,
    });
    return dialogRef.afterClosed();
  }
}

export interface DialogData {
  message: string;
  title: string;
  okButtonLabel: string;
  cancelButtonLabel: string;
}

@Component({
    template: `<span class="" tabindex="0">
    {{data}}
  </span>`,
    standalone: false
})
export class CustomSnackBarComponent {
  constructor( @Inject(MAT_SNACK_BAR_DATA) public data: any) { }
}